import { ApiClientError, requestJson, responseRecord } from './http';

export interface LegalDocument { id: string; url: string; sha256?: string; }
export interface TermsAcceptanceRecord {
  termsVersion: string;
  acceptedAt: string;
  documents: string[];
  surface: string;
}
export interface TermsAcceptanceState {
  currentVersion: string;
  documents: LegalDocument[];
  accepted: boolean;
  acceptedAt: string | null;
  /** The version this person last accepted (may be older than current: re-acceptance is needed). */
  acceptedVersion: string | null;
  /** Whether the server enforces acceptance before workspace use and paid generation. */
  required: boolean;
  /** This person's own acceptances, newest first (append-only on the server). */
  history: TermsAcceptanceRecord[];
}

function historyFrom(value: unknown): TermsAcceptanceRecord[] {
  if (value === undefined) return [];
  if (!Array.isArray(value)) malformed();
  return value.map((item: unknown) => {
    const row = responseRecord(item);
    if (typeof row.terms_version !== 'string' || !row.terms_version || typeof row.accepted_at !== 'string'
      || !row.accepted_at || !Array.isArray(row.documents)
      || !row.documents.every((doc: unknown) => typeof doc === 'string')) malformed();
    return { termsVersion: row.terms_version, acceptedAt: row.accepted_at, documents: row.documents as string[],
      surface: typeof row.surface === 'string' ? row.surface : '' };
  });
}

const PATH = '/v1/account/terms-acceptance';
function malformed(): never {
  throw new ApiClientError('malformed', 200, 'malformed_response', 'the terms response could not be confirmed');
}
function documentUrl(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password;
  } catch { return false; }
}

export async function getTermsAcceptance(): Promise<TermsAcceptanceState> {
  const row = responseRecord(await requestJson(PATH));
  if (typeof row.current_version !== 'string' || !row.current_version.trim()
    || typeof row.accepted !== 'boolean' || !Array.isArray(row.documents) || !row.documents.length) malformed();
  const documents = row.documents.map((item: unknown) => {
    const doc = responseRecord(item);
    if (typeof doc.id !== 'string' || !doc.id.trim() || !documentUrl(doc.url)) malformed();
    return typeof doc.sha256 === 'string' && /^[0-9a-f]{64}$/.test(doc.sha256)
      ? { id: doc.id, url: doc.url, sha256: doc.sha256 } : { id: doc.id, url: doc.url };
  });
  if (new Set(documents.map((doc) => doc.id)).size !== documents.length) malformed();
  if (row.accepted && (row.accepted_version !== row.current_version
    || typeof row.accepted_at !== 'string' || !row.accepted_at)) malformed();
  return {
    currentVersion: row.current_version, documents, accepted: row.accepted,
    acceptedAt: typeof row.accepted_at === 'string' ? row.accepted_at : null,
    acceptedVersion: typeof row.accepted_version === 'string' && row.accepted_version ? row.accepted_version : null,
    // An older server that does not say is treated as not enforcing; the server is the authority either way.
    required: row.required === true,
    history: historyFrom(row.history),
  };
}

/** One document's full text, for the in-app scroll box (F-6). */
export interface LegalDocumentText {
  id: string;
  title: string;
  url: string;
  sha256: string;
  /** True for the three signed documents; false for reference pages (refunds, subprocessors). */
  signed: boolean;
  text: string;
}

export async function getTermsDocuments(): Promise<{ currentVersion: string; documents: LegalDocumentText[] }> {
  const row = responseRecord(await requestJson('/v1/account/terms-documents'));
  if (typeof row.current_version !== 'string' || !row.current_version || !Array.isArray(row.documents)) malformed();
  const documents = row.documents.map((item: unknown) => {
    const doc = responseRecord(item);
    if (typeof doc.id !== 'string' || !doc.id || typeof doc.title !== 'string' || !documentUrl(doc.url)
      || typeof doc.sha256 !== 'string' || typeof doc.signed !== 'boolean' || typeof doc.text !== 'string') malformed();
    return { id: doc.id, title: doc.title, url: doc.url, sha256: doc.sha256, signed: doc.signed, text: doc.text };
  });
  return { currentVersion: row.current_version, documents };
}

/** Lower-case hex SHA-256 of the UTF-8 text, as the server computes over the published source. */
export async function sha256Hex(text: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error('this browser cannot compute document fingerprints');
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * The caller must explicitly consent; a follow-up GET confirms the current server state.
 *
 * `shown` is the digest of each document's text exactly as the screen rendered it (computed from that
 * text, not copied from the server's list). The server refuses (409 terms_text_changed) unless they
 * equal the digests of the current text.
 */
export async function recordTermsAcceptance(
  state: TermsAcceptanceState, shown: Record<string, string>,
): Promise<TermsAcceptanceState> {
  const row = responseRecord(await requestJson(PATH, {
    method: 'POST',
    // The digests of exactly the documents shown: the server refuses (409 terms_text_changed) if the text
    // changed since, so a stale screen can never record consent to text it did not show.
    body: JSON.stringify({ terms_version: state.currentVersion,
      documents: state.documents.map((doc) => doc.id), attested_adult: true,
      document_sha256: Object.fromEntries(state.documents.map((doc) => [doc.id, shown[doc.id] || ''])) }),
  }), 'acceptance');
  const acceptedDocuments = row.documents;
  if (row.terms_version !== state.currentVersion || row.attested_adult !== true
    || typeof row.recorded !== 'boolean' || typeof row.accepted_at !== 'string' || !row.accepted_at
    || !Array.isArray(acceptedDocuments) || acceptedDocuments.length !== state.documents.length
    || new Set(acceptedDocuments).size !== state.documents.length
    || !state.documents.every((doc) => acceptedDocuments.includes(doc.id))) malformed();
  return getTermsAcceptance();
}